import React, { useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationsProvider';
import { useNavigation } from '../contexts/NavigationProvider';
import { useAuth } from '../contexts/AuthProvider';
import { useOpportunities } from '../contexts/OpportunitiesProvider';
import { useMessaging } from '../contexts/MessagingProvider';
import { formatTimestamp } from '../utils/formatTimestamp';
import type { Notification } from '../types';
import UsersIcon from './icons/UsersIcon';

const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
  const { handleViewProfile, handleViewBand, handleViewOpportunity, bands, handleNavigation } = useNavigation();
  const { users } = useAuth();
  const { opportunities } = useOpportunities();
  const { setActiveConversationPartner } = useMessaging();

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const fullUser = users.find(u => u.handle === notification.user.handle);
    if (fullUser) {
        handleViewProfile(fullUser);
    }
  };

  const handleOpportunityClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.opportunity) {
      const fullOpp = opportunities.find(o => o.id === notification.opportunity?.id);
      if (fullOpp) {
          handleViewOpportunity(fullOpp);
      }
    }
  };

  const handleBandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.band) {
        const fullBand = bands.find(b => b.id === notification.band?.id);
        if (fullBand) {
            handleViewBand(fullBand);
        }
    }
  };

  const handleMainClick = () => {
    switch(notification.type) {
        case 'message':
            const sender = users.find(u => u.handle === notification.user.handle);
            if (sender) {
                setActiveConversationPartner(sender);
                handleNavigation('messages');
            }
            break;
        case 'endorse':
        case 'collaboration_add':
            const fullUser = users.find(u => u.handle === notification.user.handle);
            if (fullUser) handleViewProfile(fullUser);
            break;
        case 'create_band_prompt':
            if (notification.band) {
                const fullBand = bands.find(b => b.id === notification.band?.id);
                if (fullBand) handleViewBand(fullBand);
            }
            break;
        default:
            if (notification.opportunity) {
                const fullOpp = opportunities.find(o => o.id === notification.opportunity?.id);
                if (fullOpp) handleViewOpportunity(fullOpp);
            }
            break;
    }
  };

  const renderText = () => {
    const oppLink = <span className="font-bold text-white hover:underline cursor-pointer" onClick={handleOpportunityClick}>{notification.opportunity?.title}</span>;
    switch(notification.type) {
        case 'endorse': return ' endorsed you.';
        case 'amplify': return <> amplified your opportunity: "{oppLink}"</>;
        case 'application': return <> applied to your opportunity: "{oppLink}"</>;
        case 'withdrawal': return <> withdrew their application for: "{oppLink}"</>;
        case 'match': return <> posted an opportunity that matches your profile: "{oppLink}"</>;
        case 'accepted': return <> accepted your application for: "{oppLink}"! Congratulations!</>;
        case 'rejected': return <> did not select your application for: "{oppLink}". Keep applying!</>;
        case 'comment': return <> commented on your opportunity: "{oppLink}"</>;
        case 'invitation': return <> invited you to a private opportunity: "{oppLink}"</>;
        case 'message': return ' sent you a new message.';
        case 'collaboration_add': return ' has added you as a collaborator.';
        case 'create_band_prompt': return <> We noticed you're a member of <span className="font-bold text-white hover:underline cursor-pointer" onClick={handleBandClick}>{notification.band?.name}</span>. An official page has been created for your band!</>;
        case 'attendance': return <> is attending your event: "{oppLink}"</>;
        default: return ' did something.';
    }
  };

  return (
    <div onClick={handleMainClick} className={`p-4 flex items-start space-x-4 border-l-4 transition-colors cursor-pointer ${notification.isRead ? 'border-transparent' : 'border-brand-orange bg-brand-orange/5'}`}>
        {notification.type === 'create_band_prompt' ? (
            <div className="flex-shrink-0 h-10 w-10 bg-blue-500/20 rounded-full flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-blue-400" />
            </div>
        ) : (
            <img 
                src={notification.user.avatarUrl} 
                alt={notification.user.name} 
                className="h-10 w-10 rounded-full cursor-pointer"
                onClick={handleUserClick}
            />
        )}
        <div className="flex-1">
            <p className="text-gray-300">
                {notification.type !== 'create_band_prompt' && (
                    <span className="font-bold text-white hover:underline cursor-pointer" onClick={handleUserClick}>
                        {notification.user.name}
                    </span>
                )}
                {renderText()}
            </p>
            <p className="text-sm text-brand-muted mt-1">{formatTimestamp(notification.timestamp)}</p>
        </div>
    </div>
  );
};

const NotificationsPage: React.FC = () => {
    const { notifications, handleMarkNotificationsRead } = useNotifications();

    useEffect(() => {
        const hasUnread = notifications.some(n => !n.isRead);
        if (hasUnread) {
            handleMarkNotificationsRead();
        }
    }, [notifications, handleMarkNotificationsRead]);

    return (
        <div className="bg-brand-surface rounded-lg">
            <div className="p-6 border-b border-gray-700">
                <h2 className="text-3xl font-bold text-white">Notifications</h2>
            </div>
            {notifications.length > 0 ? (
                <div className="divide-y divide-gray-700/50">
                    {notifications.map(notification => (
                        <NotificationItem key={notification.id} notification={notification} />
                    ))}
                </div>
            ) : (
                <p className="p-8 text-center text-brand-muted">You have no notifications yet.</p>
            )}
        </div>
    );
};

export default NotificationsPage;